export const DEFAULT_RESTRICTED_FOLDER_NAMES = [
  "All Templates",
  "Stock Templates", 
  "Shared"
];

export const VALIDATION_OPTIONS = {
  RESTRICT_SYSTEM_NAMES: 'restrictSystemNames',
  BLOCK_EMOJIS: 'blockEmojis',
  BLOCK_SPECIAL_CHARS: 'blockSpecialChars'
};

// Emoji detection regex
const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]/gu;

// Special characters regex (customize as needed)
const SPECIAL_CHARS_REGEX = /[<>:"/\\|?*]/g;

/**
 * Validates folder name based on provided options
 * @param {string} folderName - The folder name to validate
 * @param {Object} options - Validation options
 * @param {boolean} options.restrictSystemNames - Whether to restrict system folder names
 * @param {boolean} options.blockEmojis - Whether to block emojis
 * @param {boolean} options.blockSpecialChars - Whether to block special characters
 * @param {Array} options.customRestrictedNames - Custom list of restricted names
 * @param {string} options.emptyMessage - Custom message for empty folder name
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateFolderName = (folderName, options = {}) => {
  const {
    restrictSystemNames = true,
    blockEmojis = true,
    blockSpecialChars = false,
    customRestrictedNames = [],
    emptyMessage = "Please enter folder name."
  } = options;

  const trimmedName = folderName.trim();
  
  // Check if empty
  if (trimmedName.length === 0) {
    return { isValid: false, message: emptyMessage };
  }
  
  // Check against restricted system names (case-insensitive)
  if (restrictSystemNames) {
    const restrictedNames = [...DEFAULT_RESTRICTED_FOLDER_NAMES, ...customRestrictedNames];
    const isRestricted = restrictedNames.some(
      restrictedName => restrictedName.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isRestricted) {
      return { 
        isValid: false, 
        message: "Please choose a different folder name." 
      };
    }
  }
  
  // Check for emojis
  if (blockEmojis && EMOJI_REGEX.test(trimmedName)) {
    return { 
      isValid: false, 
      message: "" 
    };
  }
  
  // Check for special characters
  if (blockSpecialChars && SPECIAL_CHARS_REGEX.test(trimmedName)) {
    return { 
      isValid: false, 
      message: "" 
    };
  }
  
  return { isValid: true };
};

/**
 * Preset validation configurations for different use cases
 */
export const VALIDATION_PRESETS = {
  // Strict validation - all checks enabled
  STRICT: {
    restrictSystemNames: true,
    blockEmojis: true,
    blockSpecialChars: true
  },
  
  // Standard validation - system names + emojis
  STANDARD: {
    restrictSystemNames: true,
    blockEmojis: true,
    blockSpecialChars: false
  },
  
  // Relaxed validation - only system names
  RELAXED: {
    restrictSystemNames: true,
    blockEmojis: false,
    blockSpecialChars: false
  },
  
  // Custom for email templates
  EMAIL_TEMPLATES: {
    restrictSystemNames: true,
    blockEmojis: true,
    blockSpecialChars: false,
    customRestrictedNames: ["Drafts", "Sent", "Archive"]
  },
  
  // Custom for SMS templates
  SMS_TEMPLATES: {
    restrictSystemNames: true,
    blockEmojis: false, // Allow emojis for SMS
    blockSpecialChars: false,
    customRestrictedNames: ["Scheduled", "Sent Messages"]
  }
};

export const cleanFolderNameInput = (input) => {
  return input.replace(EMOJI_REGEX, '');
};